import { Box, HStack, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { getSlaStatus, getSlaTone } from '@/lib/utils/slaStatus'
import { useThemeColors } from '@/hooks/useThemeColors'

type Props = {
  children: ReactNode
  slaDueAt?: string | null
  completedAt?: string | null
  onClick?: () => void
  cursor?: string
  /** When false, only the tone badge is shown (detail lives in children). */
  showDetail?: boolean
}

export default function TaskSlaCard({ children, slaDueAt, completedAt, onClick, cursor, showDetail = true }: Props) {
  const colors = useThemeColors()
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000)
    return () => clearInterval(id)
  }, [])

  const sla = useMemo(() => {
    if (completedAt && slaDueAt) {
      const onTime = new Date(completedAt).getTime() <= new Date(slaDueAt).getTime()
      return onTime
        ? { urgency: 'on_track' as const, toneLabel: 'On track', detail: 'Met SLA' }
        : { urgency: 'overdue' as const, toneLabel: 'Overdue', detail: 'Breached' }
    }
    const status = getSlaStatus(slaDueAt, now)
    return {
      urgency: status.urgency,
      toneLabel: status.toneLabel,
      detail: status.detail ?? status.label,
    }
  }, [slaDueAt, completedAt, now])

  const tone = getSlaTone(sla.urgency)
  const showSla = sla.urgency !== 'none' || Boolean(completedAt && slaDueAt)

  return (
    <Box
      border="1px solid"
      borderColor={showSla ? tone.border : colors.borderLight}
      borderLeftWidth={showSla ? '4px' : '1px'}
      borderLeftColor={showSla ? tone.color : colors.borderLight}
      borderRadius="md"
      p={3}
      bg={showSla ? tone.bg : colors.cardBg}
      cursor={cursor ?? (onClick ? 'pointer' : undefined)}
      onClick={onClick}
      _hover={onClick ? { bg: colors.sidebarHoverBg } : undefined}
    >
      <HStack justify="space-between" align="flex-start" gap={2} mb={showSla ? 1 : 0}>
        <Box flex="1" minW={0}>
          {children}
        </Box>
        {showSla && sla.toneLabel ? (
          <Text
            fontSize="10px"
            fontWeight="700"
            color={tone.color}
            textTransform="uppercase"
            flexShrink={0}
            lineHeight="short"
          >
            {sla.toneLabel}
          </Text>
        ) : null}
      </HStack>
      {showDetail && showSla && sla.detail ? (
        <Text fontSize="xs" color={colors.textSecondary} mt={0.5}>
          {sla.detail}
        </Text>
      ) : null}
    </Box>
  )
}
